import { Component, HostListener, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { CatalogService } from '../../tabs/catalogs-tab/services/catalog.service';
import { atLeastOneSelectedCheckbox } from '../../../../core/validators/form-array.validator';
import { LoadingSpinnerComponent } from "../../../../shared/components/loading-spinner/loading-spinner.component";
import { DataStoreService } from '../../tabs/data-lakes-tab/services/data-store.service';
import { WorkspaceService } from '../../tabs/workspaces-tab/services/workspace.service';
import { WorkspaceSuccessStates } from '../../tabs/workspaces-tab/models/workspace-provision-status.model';
import { Workspace } from '../../tabs/workspaces-tab/models/workspace.model';
import { CreateCatalogRequest } from '../../tabs/catalogs-tab/models/create-catalog.request';
import { Catalog } from '../../tabs/catalogs-tab/models/catalog.model';
import { TenantContextService } from '../../services/tenant-context.service';
import { DataStore } from '../../tabs/data-lakes-tab/models/data-store.model';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-add-catalog-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDividerModule,
    MatSelectModule,
    LoadingSpinnerComponent,
    MatCardModule
],
  templateUrl: './add-catalog-dialog.component.html',
  styleUrl: './add-catalog-dialog.component.css'
})
export class AddCatalogDialogComponent {
  dataStoreService = inject(DataStoreService);
  workspaceService = inject(WorkspaceService);
  catalogService = inject(CatalogService);
  tenantContextService = inject(TenantContextService);

  dataLakes = signal<DataStore[]>([]);
  isDataLakesLoading = signal<boolean>(false);
  dataLakesErrorMessage = signal<string>("");

  landingZones = signal<DataStore[]>([]);
  isLandingZonesLoading = signal<boolean>(false);
  landingZonesErrorMessage = signal<string>("");

  // get a list of all workspaces that have a succeeded provision status and sort alphabetically
  activeWorkspaces = this.workspaceService.workspaces()?.filter(workspace => WorkspaceSuccessStates.includes(workspace.provisionStatus)).sort((a, b) => a.workspaceName.localeCompare(b.workspaceName));
  
  addCatalogForm: FormGroup;
  isSubmitting = signal<boolean>(false);
  submissionErrorMessage = signal<string>("");

  constructor (private dialogRef: MatDialogRef<AddCatalogDialogComponent>, private fb: FormBuilder) {
    this.loadDataStoresWithoutCatalog();
    
    this.addCatalogForm = this.fb.group({
      appendTenantIdToCatalogName: [true],
      catalogName: ['', [
        Validators.required,
        Validators.pattern(/^[^ .\/]{1,255}$/)
      ]],
      dataLake: [null, [
        Validators.required,
      ]],
      landingZone: [null, [
        Validators.required,
      ]],
      workspaces: this.fb.array(
        this.activeWorkspaces!.map(() => this.fb.control(false)), 
        { validators: atLeastOneSelectedCheckbox() }
      ),
    });
  }

  loadDataStoresWithoutCatalog() {
    this.isDataLakesLoading.set(true);
    this.isLandingZonesLoading.set(true);

    this.dataLakesErrorMessage.set("");
    this.landingZonesErrorMessage.set("");

    const tenantId = this.tenantContextService.tenant()?.tenantId;
    if (!tenantId) return;
    
    this.catalogService.getUnassignedDataLakes({ tenantId: tenantId }).subscribe({
      next: (dataLakeNames: DataStore[]) => {
        this.dataLakes.set(dataLakeNames);
        this.isDataLakesLoading.set(false);
      },
      error: (error: string) => {
        console.log(error);
        this.dataLakesErrorMessage.set("Failed to get data lakes. Please try again later. ("+error+")");
        this.isDataLakesLoading.set(false);
      }
    });

    this.catalogService.getUnassignedLandingZones({ tenantId: tenantId }).subscribe({
      next: (landingZoneNames: DataStore[]) => {
        this.landingZones.set(landingZoneNames);
        this.isLandingZonesLoading.set(false);
      },
      error: (error: string) => {
        this.landingZonesErrorMessage.set("Failed to get landing zones. Please try again later. ("+error+")");
        this.isLandingZonesLoading.set(false);
      }
    });
  }

  getCatalogName(): string {
    return this.addCatalogForm.get("catalogName")?.value;
  }

  getAppendTenantIdToCatalogName(): boolean {
    return this.addCatalogForm.get("appendTenantIdToCatalogName")?.value;
  }

  getWorkspaceCheckboxControl(index: number): FormControl<boolean> {
    const formArray = this.addCatalogForm.get("workspaces") as FormArray;
    return formArray.at(index) as FormControl<boolean>;
  }

  getSelectedWorkspaces(): Workspace[] {
    const formArray = this.addCatalogForm.get("workspaces") as FormArray;
    return this.activeWorkspaces?.filter((_,i) => formArray.at(i).value) ?? [];
  }

  getSelectedDataLake(): DataStore | undefined {
    const value = this.addCatalogForm.get('dataLake')!.value;
    return this.dataLakes().find(dataLake => dataLake.dataStoreId === value);
  }

  getSelectedLandingZone(): DataStore | undefined {
    const value = this.addCatalogForm.get('landingZone')!.value;
    return this.landingZones().find(landingZone => landingZone.dataStoreId === value);
  }

  onSubmit(): void {
    this.submissionErrorMessage.set("");

    if (this.addCatalogForm.valid) {
      this.isSubmitting.set(true);

      let catalogName = this.addCatalogForm.get("catalogName")?.value;
      const dataLake = this.getSelectedDataLake();
      const landingZone = this.getSelectedLandingZone();
      const workspaceNames = this.getSelectedWorkspaces().map(workspace => workspace.workspaceName);

      if (catalogName === null ||  dataLake === undefined || landingZone === undefined || workspaceNames.length === 0) return;

      const tenantId = this.tenantContextService.tenant()?.tenantId;
      if (!tenantId) return;

      if (this.getAppendTenantIdToCatalogName()) {
        catalogName = catalogName + "_" + tenantId;
      }

      const request: CreateCatalogRequest = {
        tenantId: tenantId,
        catalogName: catalogName,
        dataLakeName: dataLake.storeName,
        landingZoneName: landingZone.storeName,
        workspaceNames: workspaceNames
      }
      
      this.catalogService.createCatalog(request).subscribe({
        next: (catalog: Catalog) => {
          this.dialogRef.close(catalog);
          this.isSubmitting.set(false);
        },
        error: (error: string) => {
          this.isSubmitting.set(false);
          this.submissionErrorMessage.set("Failed to create catalog. Please try again later. ("+error+")");
        }
      });
    }
  }

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting()) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
